"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FULFILMENT_TO_FAYNO_STATUS = exports.FAYNO_STATUS_TO_FULFILMENT = exports.FaynoStoreMenuProductToggle = exports.FaynoOrderBatch = exports.FaynoOrder = exports.FaynoOrderItem = exports.FaynoAttributes = exports.FaynoUserIdentifier = exports.FaynoOrderNumber = exports.FaynoUtcTimestamp = exports.FaynoMinorUnits = exports.FaynoPaymentStatus = exports.FaynoPaymentMethod = exports.FaynoOrderStatus = exports.FaynoStoreId = exports.FAYNO_PREFETCH_COUNT = exports.FAYNO_CONTENT_TYPE = exports.FAYNO_STORE_MENU_PRODUCTS_QUEUE = exports.FAYNO_ORDERS_POS_QUEUE = exports.FAYNO_ORDERS_MOBILE_EXCHANGE = void 0;
exports.faynoOrdersMobileQueueName = faynoOrdersMobileQueueName;
exports.faynoOrdersMobileRoutingKey = faynoOrdersMobileRoutingKey;
exports.isTerminalFaynoStatus = isTerminalFaynoStatus;
const zod_1 = require("zod");
/**
 * Fayno kitchen-station provider wire contract (FN-63, ADR-0013 §3).
 *
 * The Fayno mobile app and the kitchen station talk through Fayno's own
 * RabbitMQ broker, which answers only on the store LAN. This module pins the
 * message shapes and the topology **as the old station actually received and
 * sent them** (captured from its logs on FN-16, FN-40, FN-46, FN-50, FN-58),
 * not the snake_case draft on FN-1, which was never what the broker carried.
 *
 * Both directions use the same PascalCase `OrderBatch` envelope:
 *
 *   - inbound  — an app order arrives with `StatusName: "new"`, and every
 *     later change Fayno makes (a payment landing, the customer cancelling)
 *     arrives as the same envelope for the same `OrderNumber`;
 *   - outbound — a station status update is the same envelope with the new
 *     `StatusName` and `Items: []`; a station-created order is the full
 *     envelope with `Items`.
 *
 * ## Money
 *
 * `TotalPrice` and `PricePerItem` are **minor units carried as a JSON number
 * that may be written with a `.0`** — `13999.0` is 139.99 UAH. That is what
 * the .NET producer emits and what ~13k logged messages contain. The number
 * must be integral: a fractional value is a producer that switched to major
 * units, and the station must refuse it rather than ring a pizza at 1/100 of
 * its price. Both sides map it to `Money` at the edge and never keep the
 * float (see `FaynoMinorUnits`).
 *
 * ## Vocabulary observed versus vocabulary declared
 *
 * FN-1 declares eight statuses; the logs show six of them. All eight are
 * accepted here because the producer may start using the other two without
 * notice, and an unknown status must be a contract mismatch the terminal
 * reports, not a value it silently drops.
 */
/* -------------------------------------------------------------------------- */
/* Topology                                                                   */
/* -------------------------------------------------------------------------- */
/** Exchange the mobile app publishes orders to; also the queue-name prefix. */
exports.FAYNO_ORDERS_MOBILE_EXCHANGE = 'OrdersMobileQueues';
/** Exchange and queue the station publishes status updates and station orders to. */
exports.FAYNO_ORDERS_POS_QUEUE = 'OrdersPosQueues';
/** Queue the station publishes per-store availability toggles to. */
exports.FAYNO_STORE_MENU_PRODUCTS_QUEUE = 'Cardinal_StoreMenuProducts';
/** Message attribute the old station set — the broker payload is plain JSON text. */
exports.FAYNO_CONTENT_TYPE = 'text/plain';
/** One unacknowledged message at a time: persist-then-ack needs no more (ADR-0013 §1). */
exports.FAYNO_PREFETCH_COUNT = 1;
/** Fayno's numeric store id. Missing or zero is a station that would consume nothing. */
exports.FaynoStoreId = zod_1.z.number().int().positive();
/** `OrdersMobileQueues.<StoreId>` — durable, non-auto-delete, non-exclusive. */
function faynoOrdersMobileQueueName(storeId) {
    return `${exports.FAYNO_ORDERS_MOBILE_EXCHANGE}.${exports.FaynoStoreId.parse(storeId)}`;
}
/** `store.<StoreId>` — the binding key from the mobile exchange to the store queue. */
function faynoOrdersMobileRoutingKey(storeId) {
    return `store.${exports.FaynoStoreId.parse(storeId)}`;
}
/* -------------------------------------------------------------------------- */
/* Vocabulary                                                                 */
/* -------------------------------------------------------------------------- */
/**
 * Fayno's order status names (FN-1), in lifecycle order.
 *
 * - `new` — arrived from the app, nobody has looked at it.
 * - `accepted` — the station has it; preparation has not started.
 * - `preparing`, `ready_for_pickup`, `completed` — the kitchen flow.
 * - `rejected` — nobody collected it; Fayno's night cron and the station's own
 *   no-show expiry both publish this.
 * - `rejected_by_user` — the customer cancelled in the app.
 * - `rejected_by_fayno` — Fayno's side (the store) refused the order.
 */
exports.FaynoOrderStatus = zod_1.z.enum([
    'new',
    'accepted',
    'preparing',
    'ready_for_pickup',
    'completed',
    'rejected',
    'rejected_by_user',
    'rejected_by_fayno',
]);
/** How the customer pays. `on_pickup` is settled at the till, never on the station. */
exports.FaynoPaymentMethod = zod_1.z.enum(['on_pickup', 'online_monobank']);
/**
 * Payment fact carried from Fayno, never inferred (ADR-0013 §2).
 *
 * `hold` has only ever been seen with `online_monobank`: the bank has
 * reserved the money and Fayno has not yet confirmed it. The station may
 * prepare a held order but must not hand it over until a later message for
 * the same `OrderNumber` carries `paid` (FN-25, FN-66). `failed` travels with
 * `rejected_by_user`.
 */
exports.FaynoPaymentStatus = zod_1.z.enum(['not_required', 'paid', 'hold', 'failed']);
/* -------------------------------------------------------------------------- */
/* Primitives                                                                 */
/* -------------------------------------------------------------------------- */
/**
 * Integer minor units carried as a JSON number (`13999.0` = 139.99 UAH).
 *
 * Integral is the whole test: `13999` and `13999.0` are the same value on
 * the wire and both are accepted; `139.99` is a unit mistake and is refused.
 * Negative is refused too — nothing on a pizza order is a credit.
 */
exports.FaynoMinorUnits = zod_1.z
    .number()
    .finite()
    .nonnegative()
    .refine(Number.isInteger, 'minor units as an integral number, e.g. 13999.0 for 139.99');
/**
 * UTC ISO-8601 with an explicit `Z`, as the broker carries it.
 *
 * The station stores UTC and shows store-local time (FN-46); a timestamp
 * without an offset is how a three-hour bug gets in, so the offset is
 * required.
 */
exports.FaynoUtcTimestamp = zod_1.z.string().datetime({ offset: true });
/**
 * Fayno's order identity — a GUID. Idempotency on the station is keyed on it
 * (FN-74), and it is what the app's pickup QR carries (FN-77).
 */
exports.FaynoOrderNumber = zod_1.z.string().uuid();
/**
 * The customer as Fayno identifies them — a phone number (`38050…`). PII:
 * never on the queue display, never on a sticker, masked in every fixture.
 */
exports.FaynoUserIdentifier = zod_1.z.string().min(1).max(64);
/**
 * Free-form attributes. Observed empty in every logged message; carried
 * opaquely so a value Fayno starts sending is not dropped between hops.
 */
exports.FaynoAttributes = zod_1.z.record(zod_1.z.string().min(1), zod_1.z.union([zod_1.z.string(), zod_1.z.number(), zod_1.z.boolean(), zod_1.z.null()]));
/* -------------------------------------------------------------------------- */
/* Order batch                                                                */
/* -------------------------------------------------------------------------- */
/**
 * One line of an order. `ProductId` is Fayno's GP product id, which is also
 * the item code on the GP-compatible exchange (FN-84) — that is how the
 * station finds the sticker barcode and the picture for a line.
 */
exports.FaynoOrderItem = zod_1.z
    .object({
    ProductId: zod_1.z.number().int().positive(),
    ProductName: zod_1.z.string().min(1).max(200),
    /** Count of this product. Fayno writes `1.0`; fractional pizzas do not exist. */
    Quantity: zod_1.z.number().finite().positive().refine(Number.isInteger, 'a whole number of items'),
    PricePerItem: exports.FaynoMinorUnits,
    /** Kitchen preparation time in minutes, from Fayno's product master. */
    PreparationMinutes: zod_1.z.number().finite().nonnegative().optional(),
})
    .strict();
/**
 * One order as it travels in either direction.
 *
 * `Items` is the full line list on an inbound app order and on a station
 * order; a station status update sends `Items: []`, which is why the array
 * may be empty here and the station applies its own "an order needs lines"
 * rule at intake.
 */
exports.FaynoOrder = zod_1.z
    .object({
    OrderNumber: exports.FaynoOrderNumber,
    StatusName: exports.FaynoOrderStatus,
    PickupTime: exports.FaynoUtcTimestamp,
    StoreId: exports.FaynoStoreId,
    PaymentMethod: exports.FaynoPaymentMethod,
    PaymentStatus: exports.FaynoPaymentStatus,
    TotalPrice: exports.FaynoMinorUnits,
    UserIdentifier: exports.FaynoUserIdentifier.optional(),
    Items: zod_1.z.array(exports.FaynoOrderItem).max(200),
    Attributes: exports.FaynoAttributes.default({}),
})
    .strict()
    .superRefine((order, ctx) => {
    if (order.PaymentStatus === 'hold' && order.PaymentMethod !== 'online_monobank') {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['PaymentStatus'],
            message: 'hold is a bank reservation and only occurs with online_monobank',
        });
    }
});
/**
 * The envelope on every queue: `{"Orders":[…]}`. The old station always sent
 * and received one order per message; the array is kept because that is the
 * wire, and a producer that batches would otherwise break the consumer.
 */
exports.FaynoOrderBatch = zod_1.z
    .object({
    Orders: zod_1.z.array(exports.FaynoOrder).min(1).max(100),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Availability                                                               */
/* -------------------------------------------------------------------------- */
/**
 * Per-store availability toggle, published to `Cardinal_StoreMenuProducts`
 * when the station hides or shows an item (FN-86).
 */
exports.FaynoStoreMenuProductToggle = zod_1.z
    .object({
    ProductId: zod_1.z.number().int().positive(),
    Enabled: zod_1.z.boolean(),
    StoreId: exports.FaynoStoreId,
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Status mapping to the shared fulfilment model                              */
/* -------------------------------------------------------------------------- */
/**
 * What an inbound Fayno status means on the station's shared fulfilment
 * machine (ADR-0012 vocabulary, reused per ADR-0013 §2).
 *
 * `new` and `accepted` both land at `queued`: the station acknowledges an
 * arrival by publishing `accepted`, so an `accepted` echo is not a move.
 * Every `rejected*` is a cancellation from the station's point of view — the
 * reason lives in the Fayno status, the state is the same.
 */
exports.FAYNO_STATUS_TO_FULFILMENT = Object.freeze({
    new: 'queued',
    accepted: 'queued',
    preparing: 'preparing',
    ready_for_pickup: 'ready',
    completed: 'collected',
    rejected: 'cancelled',
    rejected_by_user: 'cancelled',
    rejected_by_fayno: 'cancelled',
});
/**
 * What the station publishes for each of its own transitions.
 *
 * - `queued` → `accepted` (the arrival acknowledgement);
 * - a station cancellation → `rejected_by_fayno` — the pizza maker is the
 *   store's side, `rejected_by_user` is reserved for the customer's own
 *   cancellation in the app;
 * - `no_show` → `rejected`, mirroring Fayno's night cron (FN-74).
 *
 * `not_released` is never published: a kitchen-station order is released the
 * moment it exists.
 */
exports.FULFILMENT_TO_FAYNO_STATUS = Object.freeze({
    queued: 'accepted',
    preparing: 'preparing',
    ready: 'ready_for_pickup',
    collected: 'completed',
    cancelled: 'rejected_by_fayno',
    no_show: 'rejected',
});
/** True when a Fayno status is terminal for the order — nothing follows it. */
function isTerminalFaynoStatus(status) {
    return status === 'completed' || status.startsWith('rejected');
}
//# sourceMappingURL=kitchen-fayno.js.map